import { useEffect, useState, type RefObject } from 'react';

const callbackMap = new WeakMap<Element, (isVisible: boolean) => void>();
const observersMap = new WeakMap<Element, IntersectionObserver>();
let defaultObserver: IntersectionObserver | null = null;

function initIntersectionObserver(parentRef?: RefObject<Element>) {
    const root = parentRef?.current ?? null;

    const intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            callbackMap.get(entry.target)?.(entry.isIntersecting);
        });
    }, {
        rootMargin: '120px',
        root,
    });

    if (root) {
        observersMap.set(root, intersectionObserver);
    } else {
        defaultObserver = intersectionObserver;
    }

    return intersectionObserver;
}

function getIntersectionObserver(parentRef?: RefObject<Element>): IntersectionObserver {
    const root = parentRef?.current ?? null;

    if (root) {
        return observersMap.get(root) || initIntersectionObserver(parentRef);
    }

    return defaultObserver || initIntersectionObserver(parentRef);
}

export default function useLazyRendering(ref: RefObject<Element>, parentRef?: RefObject<Element>) {
    const [isVisible, toggleVisibility] = useState(false);

    useEffect(() => {
        const observedElement = ref.current;

        if (!(observedElement instanceof Element)) {
            return;
        }

        const intersectionObserver = getIntersectionObserver(parentRef);
        callbackMap.set(observedElement, toggleVisibility);
        intersectionObserver.observe(observedElement);

        return () => {
            callbackMap.delete(observedElement);
            intersectionObserver.unobserve(observedElement);
        };
    }, [ref, parentRef]);

    return isVisible;
}